import { format } from 'date-fns'

import { cn } from '@/utils'
import type { GapSide, MonthlyStatus } from './types'

// Exact cents: a balance beside a gap is evidence, never abbreviated.
const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const periodText = (side: GapSide) =>
  side.periodStart.getFullYear() === side.periodEnd.getFullYear()
    ? `${format(side.periodStart, 'MMM d')} to ${format(side.periodEnd, 'MMM d, yyyy')}`
    : `${format(side.periodStart, 'MMM d, yyyy')} to ${format(side.periodEnd, 'MMM d, yyyy')}`

// Why nothing could be compared, named by the side that let the check down.
const uncheckedReason = (month: MonthlyStatus) => {
  if (!month.gapBefore && !month.gapAfter) {
    return 'There are no statement balances to compare across this gap.'
  }
  if (!month.gapBefore) {
    return 'There is no earlier statement with balances to compare with.'
  }
  if (!month.gapAfter) {
    return 'There is no later statement with balances to compare with.'
  }
  if (month.gapBefore.balance == null) {
    return 'We could not read the closing balance of the statement before it.'
  }
  if (month.gapAfter.balance == null) {
    return 'We could not read the opening balance of the statement after it.'
  }
  return 'The statements around it overlap, so there is no gap to compare across.'
}

interface Verdict {
  headline: string
  detail: string
  tone: string
}

const verdictOf = (month: MonthlyStatus): Verdict => {
  if (month.status === 'verified_gap') {
    return {
      headline: 'Balances match',
      detail:
        'The next statement opens at exactly the last closing balance, so the bank likely issued no statement.',
      tone: 'text-emerald-700',
    }
  }
  if (month.gapAmount != null) {
    const direction = month.gapAmount < 0 ? 'fell' : 'rose'
    return {
      headline: `Balance ${direction} ${currency.format(Math.abs(month.gapAmount))}`,
      detail:
        'Money moved with no statement showing it. At least one statement is missing.',
      tone: 'text-red-600',
    }
  }
  return {
    headline: "Can't check this gap",
    detail: `${uncheckedReason(month)} We can't tell if the bank skipped a month or a statement is missing.`,
    tone: 'text-foreground',
  }
}

interface SideRowProps {
  side: GapSide
  balanceLabel: string
  onOpen?: (segmentId: number) => void
}

const SIDE_ROW = 'grid w-full grid-cols-[1fr_auto] items-center gap-3 py-1.5'

const SideRow = ({ side, balanceLabel, onOpen }: SideRowProps) => {
  const content = (
    <>
      <span className="text-foreground">{periodText(side)}</span>
      <span className="text-right">
        <span className="block font-medium tabular-nums text-foreground">
          {side.balance == null ? 'Not read' : currency.format(side.balance)}
        </span>
        <span className="block text-[11px] text-ink-subtle">
          {balanceLabel}
        </span>
      </span>
    </>
  )
  if (!onOpen) return <div className={SIDE_ROW}>{content}</div>
  return (
    <button
      type="button"
      onClick={() => onOpen(side.segmentId)}
      title="Open this statement"
      className={cn(
        SIDE_ROW,
        '-mx-2 cursor-pointer rounded-md px-2 text-left hover:bg-neutral-100',
      )}
    >
      {content}
    </button>
  )
}

// The hole between the two statements, drawn as a break in the line that joins them.
const GapRow = ({ missing }: { missing: boolean }) => (
  <div className="flex items-center gap-2 py-0.5 text-[11px]">
    <span
      className={cn(
        'h-5 border-l-2 border-dashed',
        missing ? 'border-red-400' : 'border-emerald-400',
      )}
    />
    <span className={missing ? 'text-red-600' : 'text-emerald-700'}>
      No statement
    </span>
  </div>
)

interface GapEvidenceProps {
  month: MonthlyStatus
  onOpenStatement?: (segmentId: number) => void
}

// What a month with no statement was judged on: the statements on each side of the gap and the
// balances facing it. The verdict leads and the evidence sits under it.
export const GapEvidence = ({ month, onOpenStatement }: GapEvidenceProps) => {
  const verdict = verdictOf(month)
  return (
    <div className="text-xs text-ink-subtle">
      <p className={cn('text-sm font-semibold', verdict.tone)}>
        {verdict.headline}
      </p>
      <p className="mt-1 leading-relaxed">{verdict.detail}</p>
      {month.gapBefore || month.gapAfter ? (
        <div className="mt-3">
          {month.gapBefore ? (
            <SideRow
              side={month.gapBefore}
              balanceLabel="closing balance"
              onOpen={onOpenStatement}
            />
          ) : null}
          <GapRow missing={month.status === 'missing'} />
          {month.gapAfter ? (
            <SideRow
              side={month.gapAfter}
              balanceLabel="opening balance"
              onOpen={onOpenStatement}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
